// Authentication utilities
import { jwtDecode } from 'jwt-decode';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Helper function to check if token is expired
const isTokenExpired = token => {
  if (!token) {
    return true;
  }
  try {
    const { exp } = jwtDecode(token);
    if (!exp) {
      return true;
    }

    return exp < Math.floor(Date.now() / 1000);
  } catch {
    // Invalid token format
    return true;
  }
};

// Token management
export const setToken = token => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// User management
export const setUser = user => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const getUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

// Role helpers
export const getRole = () => getUser()?.role || 'user';
export const isAdmin = () => getRole() === 'admin';
export const isPartner = () => getRole() === 'partner';
// Fleet-wide features (machine management, OTA, companies, cloud sync, IMU
// production) are hidden from the company-scoped partner role
export const hasFullAccess = () => !isPartner();
export const FULL_ACCESS_ROLES = ['admin', 'user'];

// Get token expiration time
export const getTokenExpiration = () => {
  const token = getToken();
  if (!token) {
    return null;
  }

  try {
    const { exp } = jwtDecode(token);
    if (!exp) {
      return null;
    }

    return new Date(exp * 1000);
  } catch {
    return null;
  }
};

// Get time remaining until token expires (in milliseconds)
export const getTokenTimeRemaining = () => {
  const expiration = getTokenExpiration();
  if (!expiration) {
    return 0;
  }

  return Math.max(0, expiration.getTime() - Date.now());
};

// Check if token needs refresh (less than 5 minutes remaining)
export const shouldRefreshToken = () => {
  const timeRemaining = getTokenTimeRemaining();
  const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes in milliseconds
  return timeRemaining > 0 && timeRemaining < fiveMinutesInMs;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getToken();
  if (!token) {
    return false;
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    // Automatically clean up expired token
    removeToken();
    return false;
  }

  return true;
};

// Logout function
export const logout = () => {
  removeToken();
  window.location.href = '/login';
};
